import { useEffect, useMemo, useRef, useState } from 'react';

import { Combobox } from '@base-ui/react/combobox';

import { VisuallyHidden } from '@components/visually-hidden';
import { LocaleCode } from '@locale/locale-store';

import { isAddOptionItem } from './add-option';
import { useSelectTranslations } from './locale';

const PLURAL_RULES_LOCALE: Record<LocaleCode, string> = { en: 'en', is: 'is', dk: 'da' };

// The input names the highlighted option through aria-activedescendant; this
// live region tells a screen reader how many options match, and confirms a
// pick or a removal. With no match the empty-state message is the one
// announcement.
export const ResultCount = ({
  isAnnouncing,
  pickedLabel,
  removedLabel,
}: {
  isAnnouncing: boolean;
  pickedLabel?: string | null;
  removedLabel?: string | null;
}) => {
  const { lang, t } = useSelectTranslations();
  const pluralRules = useMemo(() => new Intl.PluralRules(PLURAL_RULES_LOCALE[lang]), [lang]);
  const filteredItems = Combobox.useFilteredItems<{ value: unknown }>();
  // Select appends its add row after every option.
  const lastItem = filteredItems.at(-1);
  const count =
    lastItem && isAddOptionItem(lastItem) ? filteredItems.length - 1 : filteredItems.length;
  const pluralKey = pluralRules.select(count) === 'one' ? 'result_count_one' : 'result_count_other';
  const countText = isAnnouncing && count > 0 ? t(pluralKey, { count }) : '';
  return (
    <VisuallyHidden aria-live="polite" aria-atomic>
      {pickedLabel
        ? t('option_selected', { label: pickedLabel })
        : removedLabel
          ? t('option_deselected', { label: removedLabel })
          : countText}
    </VisuallyHidden>
  );
};

// A pick or removal is announced until the list next opens or the query
// changes, when the count takes over; an open caused by the same action
// (removing a chip focuses the input, which opens the list) keeps it.
export const usePickAnnouncement = (isOpen: boolean) => {
  const [announced, setAnnounced] = useState<{ label: string; isRemoval: boolean } | null>(null);

  const seenAnnouncedRef = useRef(announced);
  useEffect(() => {
    const isFresh = announced !== seenAnnouncedRef.current;
    seenAnnouncedRef.current = announced;
    if (isOpen && !isFresh) setAnnounced(null);
  }, [isOpen, announced]);

  return {
    pickedLabel: announced && !announced.isRemoval ? announced.label : null,
    removedLabel: announced?.isRemoval ? announced.label : null,
    announcePick: (label: string) => setAnnounced(label ? { label, isRemoval: false } : null),
    announceRemoval: (label: string) => setAnnounced(label ? { label, isRemoval: true } : null),
    clearPick: () => setAnnounced(null),
  };
};
